import { Button, Drawer, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { memberPlans, type MemberHit } from "../../api/classes";
import { newIdempotencyKey } from "../../api/client";
import { bookPtSession, getPackSchedule } from "../../api/pt";
import { useServices } from "../../app/services";
import k from "./classes.module.css";
import { Failure, Field, FormError, messageOf, RowsSkeleton, styles as c, useScope } from "./common";
import { MemberPicker } from "./MemberPicker";
import { PatternForm } from "./PatternForm";
import { fromMinutes, toMinutes, WEEKDAY_NAMES } from "./time";

export function PtBookDrawer({ open, onClose, date, trainers, nameOf }: { open: boolean; onClose: () => void; date: string; trainers: string[]; nameOf: (id: string | null) => string }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(newIdempotencyKey());
  const [member, setMember] = useState<MemberHit | null>(null);
  const [plan, setPlan] = useState("");
  const [trainerId, setTrainerId] = useState(trainers[0] ?? sc.ctx.me?.staff.id ?? "");
  const [day, setDay] = useState(date);
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("11:00");
  const [error, setError] = useState<string | null>(null);
  const plans = useQuery({ queryKey: ["pt", "member-plans", ...sc.key, member?.id], queryFn: () => memberPlans(client, member!.id).then((p) => p.filter((x) => x.kind === "pt_pack")), enabled: member !== null, retry: false });
  const info = useQuery({ queryKey: ["pt", "pack-schedule", ...sc.key, plan], queryFn: () => getPackSchedule(client, plan), enabled: plan !== "", retry: false });
  const [changing, setChanging] = useState(false);
  const patterned = info.data !== undefined && info.data.bookingStyle !== "flexible" && info.data.schedule === null;
  const book = useMutation({
    mutationFn: () => bookPtSession(client, { planInstanceId: plan, trainerId, date: day, start, end }, key.current),
    onSuccess: () => { key.current = newIdempotencyKey(); void qc.invalidateQueries({ queryKey: ["pt"] }); toast.show("Session booked"); onClose(); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    if (!plan) return setError("Choose the PT pack this session uses.");
    if (!trainerId) return setError("Choose a trainer.");
    if (end <= start) return setError("The end time is before the start time.");
    setError(null);
    book.mutate();
  };
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title="Book a PT session" description={patterned ? "This pack is booked on a weekly pattern: pick it once and every session is placed." : "Pick the member, their pack and the slot. The API checks the trainer's availability."}
      footer={patterned ? undefined : <div className={c.row}><Button variant="primary" busy={book.isPending} busyLabel="Booking…" onClick={submit}>Book session</Button><Button variant="plain" onClick={onClose}>Cancel</Button></div>}>
      <div className={`${c.stack} ${c.drawerPad}`}>
        <MemberPicker picked={member?.id} onPick={(m) => { setMember(m); setPlan(""); setChanging(false); }} />
        {member ? (
          <fieldset className={`${c.stack} ${k.plain}`}>
            <legend className={c.note}>{member.name}'s PT packs</legend>
            {plans.isPending ? <RowsSkeleton rows={2} /> : plans.error ? <Failure error={plans.error} title="Could not load packs" onRetry={() => void plans.refetch()} /> : plans.data.length === 0 ? <p className={c.note}>{member.name} has no PT pack. Sell one from Billing.</p> : plans.data.map((p) => (
              <label key={p.id} className={c.plan}><input type="radio" name="pack" value={p.id} checked={plan === p.id} onChange={() => { setPlan(p.id); setChanging(false); }} /><span>PT pack · {p.sessionsTotal != null ? `${p.sessionsUsed}/${p.sessionsTotal} used` : p.status}</span></label>
            ))}
          </fieldset>
        ) : null}
        {plan && info.isPending ? <RowsSkeleton rows={2} /> : null}
        {plan && info.error ? <Failure error={info.error} title="Could not load the pack's schedule" onRetry={() => void info.refetch()} /> : null}
        {patterned && info.data ? (
          <PatternForm mode="create" planId={plan} info={info.data} trainers={trainers} nameOf={nameOf} date={day} trainerId={trainerId} onDone={onClose} />
        ) : plan && info.isPending ? null : (
          <>
            {info.data?.schedule ? (
              <section className={c.stack} aria-label="Weekly pattern">
                <p><strong>{info.data.schedule.weekdays.map((d) => WEEKDAY_NAMES[d]).join(" / ")} · {info.data.schedule.start}–{info.data.schedule.end}</strong> with {nameOf(info.data.schedule.trainerId)}. {info.data.schedule.sessionsPlanned} sessions were booked on this pattern.</p>
                {sc.ctx.has("pt.schedule_pattern") && !changing ? <div><Button onClick={() => setChanging(true)}>Change the slot</Button></div> : null}
                {changing ? <PatternForm mode="change" planId={plan} info={info.data} trainers={trainers} nameOf={nameOf} date={day} trainerId={info.data.schedule.trainerId} initial={{ preset: info.data.schedule.preset, weekdays: info.data.schedule.weekdays, start: info.data.schedule.start, end: info.data.schedule.end }} onDone={() => { setChanging(false); onClose(); }} /> : null}
                <p className={c.note}>Need one extra session? Book it below.</p>
              </section>
            ) : null}
          <Field label="Trainer" htmlFor="book-trainer"><select id="book-trainer" className={c.input} value={trainerId} onChange={(e) => setTrainerId(e.target.value)}>{trainers.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}</select></Field>
          <Field label="Date" htmlFor="book-date"><input id="book-date" type="date" className={c.input} value={day} onChange={(e) => setDay(e.target.value)} /></Field>
          <div className={c.row}>
            <Field label="Starts" htmlFor="book-start"><input id="book-start" type="time" className={c.input} value={start} onChange={(e) => { setStart(e.target.value); if (/^\d\d:\d\d$/.test(e.target.value)) setEnd(fromMinutes(Math.min(1439, toMinutes(e.target.value) + 60))); }} /></Field>
            <Field label="Ends" htmlFor="book-end"><input id="book-end" type="time" className={c.input} value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          </div>
          </>
        )}
        <FormError message={error} />
      </div>
    </Drawer>
  );
}
